import React, { useId } from 'react';
import styled from 'styled-components';
import { MobileAppFlair } from 'types/graphql';
import { CheckerboardPatternDefs, getCheckerboardPatternId, getFlairFill } from './preview/home-page/get-flair-path';

type BackgroundFlairPreviewProps = {
  flairType: MobileAppFlair;
  color: string;
};

const FLAIR_VIEWBOX = '0 0 100 50';

// Boundary line the decoration is drawn along, running from the top edge down to the right edge.
const FLAIR_START = { x: 35, y: 0 };
const FLAIR_END = { x: 100, y: 28 };

const JAGGED_TOOTH_COUNT = 16;
const JAGGED_TOOTH_HEIGHT = 3;

/** Builds a short, frequent zigzag boundary trending from FLAIR_START down to FLAIR_END. */
function getJaggedFlairPath(): string {
  const points = Array.from({ length: JAGGED_TOOTH_COUNT + 1 }, (_, i) => {
    const t = i / JAGGED_TOOTH_COUNT;
    const x = FLAIR_START.x + t * (FLAIR_END.x - FLAIR_START.x);
    const baseY = FLAIR_START.y + t * (FLAIR_END.y - FLAIR_START.y);
    const y = i % 2 === 0 ? baseY : baseY + JAGGED_TOOTH_HEIGHT;
    return `${i === 0 ? 'M' : 'L'}${x} ${y}`;
  });

  return `${points.join(' ')} L${FLAIR_END.x} 0 Z`;
}

const WAVE_HALF_PERIOD_COUNT = 6;
const WAVE_AMPLITUDE = 2;

/** Builds a smooth, evenly-oscillating boundary trending from FLAIR_START down to FLAIR_END. */
function getWavyFlairPath(): string {
  const segments = Array.from({ length: WAVE_HALF_PERIOD_COUNT }, (_, i) => {
    const startT = i / WAVE_HALF_PERIOD_COUNT;
    const endT = (i + 1) / WAVE_HALF_PERIOD_COUNT;
    const midT = (startT + endT) / 2;
    const endX = FLAIR_START.x + endT * (FLAIR_END.x - FLAIR_START.x);
    const endY = FLAIR_START.y + endT * (FLAIR_END.y - FLAIR_START.y);
    const midX = FLAIR_START.x + midT * (FLAIR_END.x - FLAIR_START.x);
    const midBaseY = FLAIR_START.y + midT * (FLAIR_END.y - FLAIR_START.y);
    const controlY = midBaseY + (i % 2 === 0 ? -WAVE_AMPLITUDE : WAVE_AMPLITUDE);
    return `Q${midX} ${controlY} ${endX} ${endY}`;
  });

  return `M${FLAIR_START.x} ${FLAIR_START.y} ${segments.join(' ')} L${FLAIR_END.x} 0 Z`;
}

function getBackgroundFlairPath(flairType: MobileAppFlair): string {
  switch (flairType) {
    case MobileAppFlair.angled:
      return `M${FLAIR_START.x} ${FLAIR_START.y} L${FLAIR_END.x} ${FLAIR_END.y} L${FLAIR_END.x} 0 Z`;
    case MobileAppFlair.curved:
      return `M${FLAIR_START.x} ${FLAIR_START.y} Q60 22 ${FLAIR_END.x} ${FLAIR_END.y} L${FLAIR_END.x} 0 Z`;
    case MobileAppFlair.jagged:
      return getJaggedFlairPath();
    case MobileAppFlair.wavy:
      return getWavyFlairPath();
    // Reuses the angled triangle -- checkerboard is a fill pattern (see `getFlairFill`) rather
    // than its own boundary shape, so the plain angled corner is the cleanest silhouette for it
    // to sit in.
    case MobileAppFlair.checkerBoard:
      return `M${FLAIR_START.x} ${FLAIR_START.y} L${FLAIR_END.x} ${FLAIR_END.y} L${FLAIR_END.x} 0 Z`;
    case MobileAppFlair.none:
      return '';
    default: {
      return '';
    }
  }
}

/** Renders a preview of a mobile app product image with the background decoration in the top right for the given decoration type. */
export function BackgroundFlairPreview({ flairType, color }: BackgroundFlairPreviewProps): JSX.Element | null {
  const instanceId = useId();

  if (flairType === MobileAppFlair.none) {
    return null;
  }

  const patternId = getCheckerboardPatternId(instanceId, color);

  return (
    <Container aria-hidden='true'>
      <FlairShape viewBox={FLAIR_VIEWBOX} preserveAspectRatio='none'>
        <CheckerboardPatternDefs color={color} patternId={patternId} />
        <FlairPath d={getBackgroundFlairPath(flairType)} $color={getFlairFill(flairType, color, patternId)} />
      </FlairShape>
      <ProductImage src='/images/product-preview-image.jpg' width={100} height={100} alt='' />
    </Container>
  );
}

const Container = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.colors.grey[95]};
  border-radius: 8px;
  display: flex;
  padding: 30px;
  justify-content: center;
  margin-top: 8px;
  overflow: hidden;
  position: relative;
  width: 100%;
  max-width: 300px;
`;

const FlairShape = styled.svg`
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
`;

const FlairPath = styled.path<{ $color: string }>`
  fill: ${({ $color }) => $color};
`;

const ProductImage = styled.img`
  height: auto;
  position: relative;
  width: 70%;
  z-index: 1;
`;
